import { useCallback, useRef } from "react";
import { t } from "@/i18n-js/instance";
import { Icon } from "@/react/components/shared/Icon";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { settingsNavPathV3 } from "@/react/helpers/urlHelpers/settingsNavPathV3";
import { useChecklistApi } from "@/react/hooks/useChecklistApi";
import { stepKeys } from "@/react/types/Checklist";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { Button } from "@circle-react-uikit/Button";
import { Modal } from "@circle-react-uikit/ModalV2";
import { PaywallSurveyForm } from "./PaywallSurveyForm";

const localeRoot = "community_onboarding.new_checklist.paywall_survey_modal";

const PaywallSurveyModalComponent = () => {
  const modal = useModal();
  const { updateChecklist } = useChecklistApi();
  const triggerFormSubmitRef = useRef<(() => Promise<void>) | null>(null);

  const handleOnClose = useCallback(async () => {
    await modal.hide();
    modal.remove();
  }, [modal]);

  const handleFormSubmit = useCallback(async () => {
    updateChecklist({ [stepKeys.setup_payments]: true });
    await handleOnClose();
  }, [updateChecklist, handleOnClose]);

  const handleSubmitClick = useCallback(async () => {
    if (triggerFormSubmitRef.current) {
      await triggerFormSubmitRef.current();
    }
  }, []);

  const handleConnectStripe = () => {
    window.open(settingsNavPathV3.paywalls.index, "_blank");
  };

  return (
    <Modal isOpen={modal.visible} onClose={handleOnClose}>
      <Modal.Overlay />
      <Modal.Content size="md">
        <Modal.Header>
          <Modal.Title size="sm">{t(`${localeRoot}.title`)}</Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={handleOnClose} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body>
          <Typography.BodyMd color="text-default">
            {t(`${localeRoot}.description`)}
          </Typography.BodyMd>

          <PaywallSurveyForm
            onSubmit={handleFormSubmit}
            triggerFormSubmitRef={triggerFormSubmitRef}
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="flex items-stretch justify-end space-x-2">
            <Button variant="secondary" onClick={handleConnectStripe}>
              <div className="flex flex-row items-center justify-center gap-1">
                <div>{t(`${localeRoot}.footer.setup_payments`)}</div>
                <Icon type="16-arrow-top-right" size={16} />
              </div>
            </Button>

            <Button onClick={handleSubmitClick} variant="primary">
              {t(`${localeRoot}.submit`)}
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const PaywallSurveyModal = ModalManager.create(() => (
  <PaywallSurveyModalComponent />
));

export const usePaywallSurveyModal = () => useModal(PaywallSurveyModal);
